import type { AdminOverview as AdminPayload, Links } from '@ops/contracts'
import { formatPeriodo, titularFounder } from '@ops/contracts'

import { irA } from '../../lib/scroll'
import { formatMetricValue, formatRelativeDays, plural } from '../../lib/format'
import {
  Button,
  HeroKpi,
  KpiCard,
  Panel,
  Section,
  Stack,
  TriageBar,
  TwoCol,
} from '../../ui'
import { deudaDeDatos } from './deuda'
import { DeudaDatosCard } from './DeudaDatosCard'
import {
  baseDe,
  baseDelFrente,
  metricaDelFrente,
  porSlug,
} from './derivar'
import { FollowupsRail } from './FollowupsRail'
import { FrenteCard, FrentesLista } from './FrenteCard'
import { FunnelPanel } from './FunnelPanel'
import { NetNewBarras, MrrLinea } from './MrrTrendChart'
import { PortfolioPanel, friccionPct } from './PortfolioPanel'
import { Vacio } from '../comunes'
import {
  claveEntidad,
  frentesDeAlertas,
  indiceCobertura,
  metricasEnMovimiento,
  metricasSanas,
  ultimaCarga,
} from './frentes'
import { loQueNoSeLee } from './narrativa'
import { tableroDeTriage } from './triage'
import type { Tablero } from './tablero'
import { Titular } from './Titular'
import { ZonaSana } from './ZonaSana'

/**
 * Overview del founder — "¿cómo está mi empresa?".
 *
 * Orden de la pantalla, que es la dirección de diseño aprobada: titular en prosa
 * → tarjeta héroe del MRR con el ARR adentro → barra de triage con los dos
 * botones de nivel pantalla → las tres zonas por urgencia → seguimientos.
 *
 * El MRR va arriba y no plegado al final: es el número por el que el founder
 * abre el panel.
 */
export interface AdminOverviewProps {
  payload: AdminPayload & { links: Links }
  tablero: Tablero
}

const ZONA_1 = 'zona-1'
const DEUDA = 'deuda-datos'

export function AdminOverviewVista({ payload, tablero }: AdminOverviewProps) {
  const { now, meta } = tablero

  const mrr = porSlug(payload.heroMetrics, 'mrr')
  const arr = porSlug(payload.heroMetrics, 'arr')
  const resto = payload.heroMetrics.filter(
    (m) => m.slug !== 'mrr' && m.slug !== 'arr',
  )

  const criticos = frentesDeAlertas(payload.alerts, 'critical')
  const atencion = frentesDeAlertas(payload.alerts, 'warning')
  // Los frentes de atención se dibujan en la Zona 1 junto a los críticos: el
  // chip que los cuenta lleva ahí, y un contador que no se puede auditar
  // clickeándolo es peor que no tenerlo.
  const enZona1 = [...criticos, ...atencion]
  /*
   * La deuda sale de `payload.alerts` y no de `heroMetrics`. La fila héroe son
   * cinco métricas; la API alerta sobre las veintiuna del rol. Con el cálculo
   * viejo, seis `metric_missing` y un `data_expired` que venían en el mismo
   * payload no llegaban nunca a la pantalla y el bloque titulaba "No hay deuda
   * de datos".
   */
  const deuda = deudaDeDatos(payload.alerts, payload.heroMetrics, meta.valorDe)
  const faltantes = deuda.sinDato
  const viejas = deuda.vencidas
  const sanas = metricasSanas(resto)
  const moviendose = metricasEnMovimiento(resto)

  const cobertura = indiceCobertura(payload.followupsByOwner, payload.links)
  const huerfanos = criticos.filter(
    (f) => !cobertura.has(claveEntidad(f.entityType, f.entityId)),
  ).length

  const pendientes =
    payload.portfolio.porEstado.find(
      (e) => e.estado === 'pendiente_implementacion',
    )?.cantidad ?? 0
  const friccion = friccionPct(payload.portfolio)

  const triage = tableroDeTriage(
    [
      {
        severidad: 'critical',
        cantidad: criticos.length,
        label: 'frentes rotos',
        labelSingular: 'frente roto',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'warning',
        cantidad: atencion.length,
        label: 'frentes en atención',
        labelSingular: 'frente en atención',
        onClick: () => irA(ZONA_1),
      },
      {
        severidad: 'unknown',
        cantidad: faltantes.length,
        label: 'números sin dato',
        labelSingular: 'número sin dato',
        onClick: () => irA(DEUDA),
      },
      {
        severidad: 'warning',
        cantidad: viejas.length,
        label: 'cargas vencidas',
        labelSingular: 'carga vencida',
        onClick: () => irA(DEUDA),
      },
      {
        severidad: 'ok',
        cantidad: sanas.length,
        label: 'frentes en target',
        labelSingular: 'frente en target',
      },
    ],
    'Ningún frente pide atención en el período',
  )

  const cargado = ultimaCarga(payload.heroMetrics)

  return (
    <Stack gap="lg">
      <Titular
        titular={{
          ...titularFounder(payload, now),
          // `titularFounder` vive en el contrato y arma "lo que no se lee" con
          // `heroMetrics`, así que decía "todas las métricas tienen dato" con
          // siete alertas de deuda en el mismo payload. La oración se deriva de
          // la misma deuda que dibuja el bloque de abajo.
          loQueNoSeLee: loQueNoSeLee(deuda, now),
        }}
        periodo={formatPeriodo(payload.periodo)}
      />

      {mrr === null ? (
        <Vacio icono={null}
          mensaje="El período no tiene ninguna métrica de revenue en el tablero"
          detalle="El catálogo de métricas de Admin define qué entra en la fila héroe."
          punteada
        />
      ) : (
        <HeroKpi
          metric={mrr}
          secundaria={arr}
          base={baseDe(mrr)}
          direccion={meta.direccionDe(mrr.slug)}
          aside={<MrrLinea trend={payload.mrrTrend} />}
          onLoadData={() => tablero.cargarDato(mrr)}
          now={now}
          acciones={
            <>
              <Button
                variante="secundario"
                onClick={() => tablero.verDetalle(mrr)}
              >
                Ver definición y umbrales
              </Button>
              <Button
                variante="sutil"
                icono="mas"
                onClick={() => tablero.seguirMetrica(mrr)}
              >
                Asignar seguimiento
              </Button>
            </>
          }
        />
      )}

      <TriageBar
        estado={triage.estado}
        titular={triage.titular}
        conteos={triage.conteos}
        meta={
          cargado === null
            ? 'ninguna métrica tiene carga registrada'
            : `carga más reciente: ${formatRelativeDays(cargado, now)}`
        }
        acciones={
          <>
            <Button variante="primario" onClick={() => irA(ZONA_1)}>
              Atacar el cuello de botella ({formatMetricValue(pendientes, 'count')}{' '}
              {plural(pendientes, 'cuenta', 'cuentas')})
            </Button>
            <Button variante="secundario" onClick={() => irA(DEUDA)}>
              Cargar los datos que faltan (
              {formatMetricValue(faltantes.length + viejas.length, 'count')})
            </Button>
          </>
        }
        className="rounded-card border border-border"
      />

      <Section
        id={ZONA_1}
        kicker="Zona 1"
        titulo="Lo que está roto"
        bajada={
          <>
            <b>{criticos.length}</b>{' '}
            {plural(criticos.length, 'frente crítico', 'frentes críticos')} y{' '}
            <b>{faltantes.length + viejas.length}</b>{' '}
            {plural(
              faltantes.length + viejas.length,
              'número que no se puede leer',
              'números que no se pueden leer',
            )}
            . No poder medir también es estar roto, así que la deuda de datos
            entra acá y no en una nota al pie.{' '}
            {huerfanos > 0 ? (
              <>
                <b>{huerfanos}</b>{' '}
                {plural(
                  huerfanos,
                  'frente todavía no tiene seguimiento en Plane',
                  'frentes todavía no tienen seguimiento en Plane',
                )}
                .
              </>
            ) : (
              'Todos los frentes críticos ya tienen seguimiento en Plane.'
            )}
          </>
        }
      >
        <TwoCol
          ratio="rail"
          asideSticky
          aside={
            <FollowupsRail
              grupos={payload.followupsByOwner}
              links={payload.links}
              titulo="Seguimientos abiertos"
              vacio="Nadie tiene seguimientos abiertos. Cuando asignes uno desde un frente, aparece acá con su estado de Plane."
            />
          }
        >
          <Stack gap="md">
            <FrentesLista
              frentes={enZona1}
              vacio={
                <Vacio icono={null}
                  mensaje="Ningún frente crítico en el período"
                  detalle="No hay métricas fuera de umbral ni cuentas en retención."
                />
              }
            >
              {(frente) => (
                <FrenteCard
                  key={frente.id}
                  frente={frente}
                  owner={
                    frente.entityType === 'metric'
                      ? meta.ownerDe(frente.entityId)
                      : null
                  }
                  cobertura={cobertura.get(
                    claveEntidad(frente.entityType, frente.entityId),
                  )}
                  onCrearSeguimiento={tablero.seguirFrente}
                  metric={metricaDelFrente(payload.heroMetrics, frente)}
                  base={baseDelFrente(payload.heroMetrics, frente)}
                  direccion={
                    frente.entityType === 'metric'
                      ? meta.direccionDe(frente.entityId)
                      : undefined
                  }
                  now={now}
                  onVerDetalle={tablero.verDetalle}
                />
              )}
            </FrentesLista>

            <DeudaDatosCard
              id={DEUDA}
              sinDato={faltantes}
              vencidas={viejas}
              onCargar={tablero.cargarDato}
              ownerDe={meta.ownerDe}
              now={now}
            />
          </Stack>
        </TwoCol>
      </Section>

      <Section
        kicker="Zona 2"
        titulo="Lo que se está moviendo"
        bajada={
          <>
            Todavía no son un problema, pero mueven el mes:{' '}
            {friccion === null ? (
              'el portfolio no tiene cuentas cargadas'
            ) : (
              <>
                el <b>{formatMetricValue(friccion, 'percent')}</b> del portfolio
                está sin implementar o en retención
              </>
            )}
            , y el funnel de {formatPeriodo(payload.funnel.periodo)} publica cada
            conversión con su base para poder recalcularla a mano.
          </>
        }
      >
        <Stack gap="md">
          <TwoCol ratio="igual" aside={<FunnelPanel funnel={payload.funnel} />}>
            <PortfolioPanel portfolio={payload.portfolio} />
          </TwoCol>

          <Panel
            titulo="Cómo se compone el mes: MRR nuevo contra MRR perdido"
            subtitulo="La barra de pérdida va abajo del eje: un mes de bajas dibujado para arriba se lee como crecimiento."
            padding="sm"
          >
            <NetNewBarras trend={payload.mrrTrend} />
          </Panel>

          {moviendose.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {moviendose.map((metric) => (
                <KpiCard
                  key={metric.slug}
                  metric={metric}
                  base={baseDe(metric)}
                  direccion={meta.direccionDe(metric.slug)}
                  onLoadData={() => tablero.cargarDato(metric)}
                  onOpenDetail={() => tablero.verDetalle(metric)}
                  now={now}
                />
              ))}
            </div>
          ) : null}
        </Stack>
      </Section>

      <Section
        kicker="Zona 3"
        titulo="Lo que está sano"
        bajada="Colapsado a propósito: si un frente está en target no pide atención, y llenar la pantalla de tarjetas verdes es lo que tapa lo que está roto."
        regla={false}
        espaciado="sm"
      >
        <ZonaSana
          metricas={sanas}
          direccionDe={meta.direccionDe}
          onOpenDetail={tablero.verDetalle}
          now={now}
        />
      </Section>
    </Stack>
  )
}

